// Worker 的地址
const MH_CUSTOMER_API_BASE = "https://mh-customerdata.luc-canopus.workers.dev";
// 请求配置：超时时间、重试次数
const REQUEST_CONFIG = {
  timeout: 10000, // 10秒超时
  retryTimes: 1,  // 失败重试1次
};

// 工具函数：带超时的 fetch
const fetchWithTimeout = (url, options = {}, timeout = REQUEST_CONFIG.timeout) => {
  return Promise.race([
    fetch(url, options),
    new Promise((_, reject) => 
      setTimeout(() => reject(new Error(`请求超时（${timeout}ms）`)), timeout)
    )
  ]);
};

// 工具函数：带重试的异步请求
const requestWithRetry = async (fn) => {
  let attempt = 0;
  while (attempt <= REQUEST_CONFIG.retryTimes) {
    try {
      return await fn();
    } catch (err) {
      attempt++;
      if (attempt > REQUEST_CONFIG.retryTimes) {
        throw err;
      }
      console.warn(`[mhCustomerData] 请求失败，重试第 ${attempt} 次:`, err.message);
      await new Promise(resolve => setTimeout(resolve, 300 * attempt)); // 指数退避
    }
  }
};

// 对外暴露一个全局对象，方便在别的脚本里调用
window.mhCustomerData = {
  // 读取 customer 的 custom.data（JSON metafield）
  async read(onError) {
    try {
      const customerId = window.__CUSTOMER_ID__;
      if (!customerId) {
        const errMsg = "[mhCustomerData.read] 未登录用户，没有 customer.id";
        console.log(errMsg);
        onError && onError(errMsg);
        return null;
      }

      const url = new URL(MH_CUSTOMER_API_BASE);
      url.searchParams.set("owner_resource", "customer");
      url.searchParams.set("owner_id", customerId);

      const res = await requestWithRetry(() => 
        fetchWithTimeout(url.toString(), { method: "GET" })
      );
      const data = await res.json();

      if (!data.ok) {
        const errMsg = `[mhCustomerData.read] 读取 metafield 失败: ${data.error || '未知错误'}`;
        console.error(errMsg, data);
        onError && onError(errMsg, data);
        return null;
      }

      // data.value 要么是 null，要么是你存的 JSON 对象
      return data.value;
    } catch (err) {
      const errMsg = `[mhCustomerData.read] 网络/系统错误: ${err.message}`;
      console.error(errMsg);
      onError && onError(errMsg);
      return null;
    }
  },

  // 写入 / 覆盖 customer 的 custom.data（JSON metafield）
  async write(obj, onError) {
    try {
      const customerId = window.__CUSTOMER_ID__;
      if (!customerId) {
        const errMsg = "[mhCustomerData.write] 未登录用户，不能写 customer metafield";
        console.log(errMsg);
        onError && onError(errMsg);
        return false;
      }

      if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
        const errMsg = "[mhCustomerData.write] 传入的 value 必须是普通对象";
        console.error(errMsg);
        onError && onError(errMsg);
        return false;
      }

      const res = await requestWithRetry(() => 
        fetchWithTimeout(MH_CUSTOMER_API_BASE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            owner_resource: "customer",
            owner_id: customerId,
            value: obj,
          }),
        })
      );

      const data = await res.json();
      if (!data.ok) {
        const errMsg = `[mhCustomerData.write] 写入 metafield 失败: ${data.error || '未知错误'}`;
        console.error(errMsg, data);
        onError && onError(errMsg, data);
        return false;
      }

      return true;
    } catch (err) {
      const errMsg = `[mhCustomerData.write] 网络/系统错误: ${err.message}`;
      console.error(errMsg);
      onError && onError(errMsg);
      return false;
    }
  },

  // 增量更新（新增：只修改指定字段，不覆盖整个对象）
  async update(fields, onError) {
    try {
      // 先读取现有数据
      const existingData = await this.read(onError);
      if (existingData === null && Object.keys(fields).length === 0) {
        return false;
      }
      // 合并现有数据和新字段（新字段覆盖旧字段）
      const newData = { ...(existingData || {}), ...fields };
      // 写入合并后的数据
      return await this.write(newData, onError);
    } catch (err) {
      const errMsg = `[mhCustomerData.update] 增量更新失败: ${err.message}`;
      console.error(errMsg);
      onError && onError(errMsg);
      return false;
    }
  },
};